/* assets/baby-poster-renderer.js
   Baby Poster renderer:
   - Preview: canvas (fast)
   - Export: JPG preview + PDF @ 300 DPI, exact mm size
   - Layers:
       1) Full poster background (WHITE)
       2) Colored RECT (artboard) at the top
       3) Baby inside colored rect (clamped, NEVER squished)
       4) Name + Meta on the WHITE area UNDER the rect

   ✅ MULTI-BABY (twins / triplets): renderMultiPoster draws N babies on one
   artboard (N equal columns, shared true-scale factor, common baseline, one
   caption per column). 100x70 is landscape with a 1800x1200 design space
   (getDesignSize); every portrait size keeps 900x1200.

   ✅ modelCm-only scaling:
   Assumption: image naturalHeight corresponds to modelCm (full height == baby length).
   If assets have different padding/crop, this will vary between models.

   ✅ UPDATED FOR SHOPIFY BLOCKS:
   - Keys are dynamic (block.id)
   - Models are created/updated at runtime via setBabyModelAssetUrl + setBabyModelCalibration
   - cmToPosterFactor may come in as string (e.g. "0.82" or "0,82")
*/

(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.BabyPosterRenderer = factory();
})(typeof self !== "undefined" ? self : this, function () {
  // "Design space" (logical coordinates)
  const DESIGN_W = 900;
  const DESIGN_H = 1200;

  // Print sizes in mm
  const SIZE_MM = {
    "30x40": { w: 300, h: 400 },
    "40x50": { w: 400, h: 500 },
    "50x70": { w: 500, h: 700 },
    "100x70": { w: 1000, h: 700 }, // landscape (triplets)
  };

  // Landscape sizes get a wider design space with the SAME 1200 height, so the
  // footer/text metrics and the px-per-cm ratio carry over from 50x70 unchanged
  // (100x70 is exactly two 50x70 side by side). Every other size is portrait.
  const DESIGN_SIZES = {
    "100x70": { w: 1800, h: 1200 },
  };

  function getDesignSize(sizeKey) {
    if (sizeKey && Object.prototype.hasOwnProperty.call(DESIGN_SIZES, sizeKey)) {
      const d = DESIGN_SIZES[sizeKey];
      return { w: d.w, h: d.h };
    }
    return { w: DESIGN_W, h: DESIGN_H };
  }

  // With blocks, keys are dynamic. We'll keep a dynamic registry.
  let DEFAULT_BABY_KEY = null;

  // Defaults used when a model isn't fully configured from the section
  const DEFAULT_MODEL_CM = 57;
  const DEFAULT_CM_TO_POSTER_FACTOR = 0.82;
  const DEFAULT_DIRECTION = "left";

  // Dynamic map: key -> model
  const BABY_MODELS = {};

  const DEFAULT_STYLE = {
    // Colors
    rectColor: "#EDEBE7",
    posterBgColor: "#FFFFFF",
    textColor: "#111111",
    fontFamily: "RockwellPoster",

    // Outer margins for rect
    marginTop: 66,
    marginLeft: 66,
    marginRight: 66,

    // White footer under rect for text
    footerHeight: 114,
    footerGap: 22,
    footerPaddingX: 70,
    footerPaddingBottom: 30,

    // Text styling
    nameSize: 13,
    metaSize: 9,
    letterSpacingName: 8,
    letterSpacingMeta: 7,

    // Text positions inside footer (measured from footer bottom)
    NAME_Y_FROM_FOOTER_BOTTOM: 68,
    META_Y_FROM_FOOTER_BOTTOM: 45,

    // Baby shadow
    babyShadow: true,

    // Multi-baby poster (renderMultiPoster only; the single poster ignores these).
    // Fractions are of the artboard width unless noted. The babies form one group
    // centred on the artboard, measured on the visible drawings.
    // "auto": twins (2 babies) always get the compact group / stagger logic below (never
    // equal columns); every other count keeps plain equal columns (each baby's whole
    // image centred in its column) unless a baby would overflow its column, then the
    // compact group / stagger takes over. "side" | "stagger" | "columns" force one.
    multiLayout: "auto", // "auto" | "side" | "stagger" | "diagonal" | "columns" | "classic" (auto before the twins change)
    multiInnerGap: 0.03, // gap between neighbouring babies
    multiDiagonalGap: 0.11, // twins diagonal: distance between the two drawings (share of the baby height, at the reference size)
    multiDiagonalDrop: 0.6, // twins diagonal: the lower baby sits this share of the baby height lower (at the reference size, capped by the margins)
    multiDiagonalRefHeight: 0.6, // twins diagonal: reference size = taller drawing / artboard height (about 50 cm on 50x70)
    multiDiagonalGrowth: 2.5, // twins diagonal: how much faster than the babies drop and gap grow (0 = in step with them)
    multiMinMargin: 0.055, // smallest free space at the left / right artboard edge
    multiMaxOverlap: 0.1, // stagger: bounding boxes may overlap by this share of the narrower baby
    multiRowClearance: 0.01, // stagger: free space kept between two drawings on any shared row
    multiStaggerMarginY: 0.04, // stagger: free space above / below (share of the artboard HEIGHT)
    // push (true scale does not fit side by side or staggered): the margins may
    // shrink to these before any baby is drawn smaller than true size
    multiPushMinMargin: 0.0275, // left / right, share of the artboard width
    multiPushMarginY: 0.02, // above / below, share of the artboard HEIGHT
    // Captions: min gap between two captions centred under their babies (else equal
    // columns), and inner padding of a caption in a column (design units).
    multiCaptionGap: 24,
    multiCaptionPadX: 16,

    // Debug
    debug: true,
  };

  // -------------------------
  // Helpers
  // -------------------------
  function getArtboardRect(style, design) {
    const dw = design ? design.w : DESIGN_W;
    const dh = design ? design.h : DESIGN_H;
    const x = style.marginLeft;
    const y = style.marginTop;
    const w = dw - style.marginLeft - style.marginRight;
    const h = dh - style.marginTop - style.footerGap - style.footerHeight;
    return { x, y, w, h: Math.max(1, h) };
  }

  function getFooterRect(style, artboard, design) {
    const dw = design ? design.w : DESIGN_W;
    const dh = design ? design.h : DESIGN_H;
    const x = 0;
    const y = artboard.y + artboard.h + style.footerGap;
    const w = dw;
    const h = Math.max(1, dh - y);
    return { x, y, w, h };
  }

  function safeFilename(s) {
    return String(s || "")
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9\-_]/g, "");
  }

  function downloadDataUrl(dataUrl, filename) {
    const a = document.createElement("a");
    a.download = filename;
    a.href = dataUrl;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function mmToPx(mm, dpi) {
    return Math.round((mm / 25.4) * dpi);
  }

  function makeOffscreenCanvas(pxW, pxH, sizeKey) {
    const off = document.createElement("canvas");
    off.width = pxW;
    off.height = pxH;
    const offCtx = off.getContext("2d");

    offCtx.imageSmoothingEnabled = true;
    offCtx.imageSmoothingQuality = "high";

    // map DESIGN coords -> pixels (can be non-uniform to fit print size)
    const design = getDesignSize(sizeKey);
    offCtx.setTransform(pxW / design.w, 0, 0, pxH / design.h, 0, 0);

    return { off, offCtx };
  }

  // ✅ Preview canvas setup (ratio per sizeKey)
  function setupPreviewCanvas(canvas, ctx, dprMax = 3, sizeKey) {
    const dpr = Math.max(1, Math.min(dprMax, window.devicePixelRatio || 1));
    const rect = canvas.getBoundingClientRect();
    const cssW = Math.max(1, Math.floor(rect.width));

    let ratio = DESIGN_H / DESIGN_W; // fallback
    if (sizeKey) {
      const mm = SIZE_MM[sizeKey] || SIZE_MM["30x40"];
      ratio = (Number(mm.h) || 400) / (Number(mm.w) || 300);
    }

    const cssH = Math.max(1, Math.floor(cssW * ratio));
    canvas.style.height = `${cssH}px`;
    canvas.width = Math.floor(cssW * dpr);
    canvas.height = Math.floor(cssH * dpr);

    const design = getDesignSize(sizeKey);
    ctx.setTransform(
      canvas.width / design.w,
      0,
      0,
      canvas.height / design.h,
      0,
      0,
    );

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
  }

  function setupPreviewCanvasForSize(canvas, ctx, sizeKey, dprMax = 3) {
    return setupPreviewCanvas(canvas, ctx, dprMax, sizeKey);
  }

  async function ensureFontLoaded(style = DEFAULT_STYLE) {
    if (!document.fonts || !document.fonts.load) return;
    await document.fonts.load(
      `400 ${style.nameSize}px ${style.fontFamily}`,
      "ABCDEFGHIJKLMNOPQRSTUVWXYZÅÄÖ",
    );
    await document.fonts.load(
      `400 ${style.metaSize}px ${style.fontFamily}`,
      "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZÅÄÖ",
    );
    if (document.fonts.ready) await document.fonts.ready;
  }

  async function loadImageTo(imgEl, url) {
    return new Promise((resolve, reject) => {
      imgEl.onload = async () => {
        try {
          if (imgEl.decode) await imgEl.decode();
        } catch (_) {}
        resolve();
      };
      imgEl.onerror = reject;
      imgEl.src = url;
    });
  }

  // Width of `text` drawn by drawSpacedText with the ctx's current font.
  function spacedTextWidth(ctx, text, letterSpacing) {
    const chars = String(text || "").split("");
    let totalWidth = 0;
    for (const ch of chars)
      totalWidth += ctx.measureText(ch).width + letterSpacing;
    return Math.max(0, totalWidth - letterSpacing);
  }

  function drawSpacedText(ctx, text, centerX, y, letterSpacing) {
    const chars = String(text || "").split("");
    const totalWidth = spacedTextWidth(ctx, text, letterSpacing);

    let x = centerX - totalWidth / 2;
    for (const ch of chars) {
      ctx.fillText(ch, x, y);
      x += ctx.measureText(ch).width + letterSpacing;
    }
  }

  function fitTextSize(ctx, text, maxWidth, startPx, minPx, fontFamily) {
    let px = startPx;
    while (px >= minPx) {
      ctx.font = `${px}px ${fontFamily}, RockwellPoster`;
      if (ctx.measureText(text).width <= maxWidth) return px;
      px -= 0.5;
    }
    return minPx;
  }

  // -------------------------
  // Model parsing / registry
  // -------------------------
  function parseFactor(val, fallback = DEFAULT_CM_TO_POSTER_FACTOR) {
    // supports number, "0.82", "0,82"
    if (val == null) return fallback;
    if (typeof val === "number" && isFinite(val)) return val;

    const s = String(val).trim();
    if (!s) return fallback;

    const n = parseFloat(s.replace(",", "."));
    return isFinite(n) && n > 0 ? n : fallback;
  }

  function parseModelCm(val, fallback = DEFAULT_MODEL_CM) {
    const n = Number(val);
    return isFinite(n) && n > 0 ? n : fallback;
  }

  function ensureModel(key) {
    let k = key || DEFAULT_BABY_KEY;
    if (!k) k = "__default__";

    if (!BABY_MODELS[k]) {
      BABY_MODELS[k] = {
        key: k,
        label: k,
        assetUrl: "",
        modelCm: DEFAULT_MODEL_CM,
        cmToPosterFactor: DEFAULT_CM_TO_POSTER_FACTOR,
        direction: DEFAULT_CM_TO_POSTER_FACTOR,
      };
    }

    if (!DEFAULT_BABY_KEY || DEFAULT_BABY_KEY === "__default__") {
      DEFAULT_BABY_KEY = k;
    }

    return BABY_MODELS[k];
  }

  function setBabyModelAssetUrl(key, url) {
    const m = ensureModel(key);
    m.assetUrl = url || "";
  }

  function setBabyModelCalibration(
    key,
    { modelCm, cmToPosterFactor, direction } = {},
  ) {
    const m = ensureModel(key);
    m.modelCm = parseModelCm(modelCm, m.modelCm);

    // NOTE: cmToPosterFactor may be string
    m.cmToPosterFactor = parseFactor(cmToPosterFactor, m.cmToPosterFactor);
    m.direction = direction;
  }

  function getBabyModel(key) {
    if (key && BABY_MODELS[key]) return BABY_MODELS[key];
    if (DEFAULT_BABY_KEY && BABY_MODELS[DEFAULT_BABY_KEY])
      return BABY_MODELS[DEFAULT_BABY_KEY];
    return ensureModel(key);
  }

  function upsertBabyModel(
    key,
    { assetUrl, modelCm, cmToPosterFactor, startInverted, direction },
  ) {
    BABY_MODELS[key] = {
      assetUrl: assetUrl,
      modelCm: modelCm,
      direction: direction,
      startInverted: startInverted,
      cmToPosterFactor: cmToPosterFactor, // string OK
    };
  }

  function setDefaultBabyKey(key) {
    DEFAULT_BABY_KEY = key;
  }

  // -------------------------
  // Core math helpers
  // -------------------------
  function getPaperHeightMm(sizeKey) {
    const mm = SIZE_MM[sizeKey] || SIZE_MM["30x40"];
    return Number(mm.h) || 400;
  }

  function getPosterPxPerCm(ctx, sizeKey) {
    const tr = ctx.getTransform();
    const sy = tr.d || 1; // DESIGN->px scale in Y
    const posterPxH = DESIGN_H * sy; // full poster height in device pixels
    const paperHeightCm = getPaperHeightMm(sizeKey) / 10;
    return paperHeightCm > 0 ? posterPxH / paperHeightCm : 0;
  }

  // -------------------------
  // Baby render (NEVER squish)
  // + flipX support (mirror)
  // -------------------------

  // Real-size maths, shared by the single and the multi poster: how big this
  // baby is at true scale (before any artboard/column clamp), in canvas pixels.
  function computeBabyMetrics(ctx, babyImg, input, sizeKey) {
    const model = getBabyModel(input && input.babyType);

    // Transform scales (may be non-uniform)
    const tr = ctx.getTransform();
    const sx = tr.a || 1;
    const sy = tr.d || 1;

    // Source image px
    const iw = babyImg.naturalWidth || babyImg.width;
    const ih = babyImg.naturalHeight || babyImg.height;

    // Input cm (user)
    const inputCm = isFinite(Number(input.lengthCm))
      ? Number(input.lengthCm)
      : 0;

    // Per-model factor (cm -> poster cm)  (can be string)
    const factor = parseFactor(model.cmToPosterFactor, 1);
    const targetPosterCm = inputCm * factor;

    // Poster px per cm (preview/export consistent)
    const pxPerCm = getPosterPxPerCm(ctx, sizeKey);
    const targetPosterPxLen = targetPosterCm * pxPerCm;

    // Model cm (the “reference baby length” for the asset)
    const modelCm = parseModelCm(model.modelCm, inputCm || 1);

    // Source px per cm (ASSUMPTION: image height == modelCm)
    const sourcePxPerCm = ih / modelCm;

    // Target scale in pixel space
    const targetScalePx = ih > 0 ? targetPosterPxLen / ih : 1;

    return {
      model,
      sx,
      sy,
      iw,
      ih,
      inputCm,
      factor,
      targetPosterCm,
      pxPerCm,
      targetPosterPxLen,
      modelCm,
      sourcePxPerCm,
      drawPxW: iw * targetScalePx,
      drawPxH: ih * targetScalePx,
    };
  }

  // Print originals (admin only): the storefront draws from a small preview file of
  // each design, the print from the full-resolution original. The admin hands the
  // renderer the image the customer's preview was drawn from (all sizing and the
  // twins/triplets layout come from it) and registers the original as its print
  // source, which paintBaby draws at exactly that place. Nothing registered = the
  // image itself is drawn, as before.
  const printSources = typeof WeakMap === "function" ? new WeakMap() : null;

  function setPrintSource(layoutImg, printImg) {
    if (!printSources || !layoutImg) return;
    if (printImg) printSources.set(layoutImg, printImg);
    else printSources.delete(layoutImg);
  }

  // Draws the baby at x,y,drawW,drawH (DESIGN space), mirrored around its own
  // centre when flipX.
  function paintBaby(ctx, babyImg, x, y, drawW, drawH, flipX, style) {
    const cx = x + drawW / 2;
    const cy = y + drawH / 2;

    ctx.save();

    if (style.babyShadow) {
      ctx.shadowColor = "rgba(0,0,0,0.12)";
      ctx.shadowBlur = 16;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 8;
    }

    ctx.translate(cx, cy);
    if (flipX) ctx.scale(-1, 1);
    const src = (printSources && printSources.get(babyImg)) || babyImg;
    ctx.drawImage(src, -drawW / 2, -drawH / 2, drawW, drawH);

    ctx.restore();
  }

  function drawBabyInsideArtboard(ctx, babyImg, input, style, artboard) {
    if (!babyImg || !(babyImg.naturalWidth || babyImg.width)) return;

    // Selected paper size
    const sizeKey = input.sizeKey || "30x40";

    const m = computeBabyMetrics(ctx, babyImg, input, sizeKey);
    const { model, sx, sy, iw, ih, inputCm, factor, targetPosterCm } = m;
    const { pxPerCm, targetPosterPxLen, modelCm, sourcePxPerCm } = m;

    let drawPxW = m.drawPxW;
    let drawPxH = m.drawPxH;

    // Clamp to artboard (physical constraint)
    const artPxW = artboard.w * sx;
    const artPxH = artboard.h * sy;

    let clamped = false;
    if (drawPxH > artPxH) {
      const f = artPxH / drawPxH;
      drawPxW *= f;
      drawPxH *= f;
      clamped = true;
    }
    if (drawPxW > artPxW) {
      const f = artPxW / drawPxW;
      drawPxW *= f;
      drawPxH *= f;
      clamped = true;
    }

    // Center in pixel-space
    const artPxX = artboard.x * sx;
    const artPxY = artboard.y * sy;

    const xPx = artPxX + (artPxW - drawPxW) / 2;
    const yPx = artPxY + (artPxH - drawPxH) / 2;

    // Convert back to DESIGN-space for drawImage
    const x = xPx / sx;
    const y = yPx / sy;
    const drawW = drawPxW / sx;
    const drawH = drawPxH / sy;

    let flipX = !!(input && input.direction !== model.direction);
    console.log(flipX, model.startInverted, input.direction, model.direction);

    if (style.debug) {
      const achievedPosterCm = pxPerCm > 0 ? drawPxH / pxPerCm : 0;
      console.log("[Baby render debug - dynamic models + string factor]", {
        sizeKey,
        babyType: input && input.babyType,
        inputCm: Number(inputCm.toFixed(2)),
        modelCm,
        cmToPosterFactorRaw: model.cmToPosterFactor,
        cmToPosterFactor: factor,
        targetPosterCm: Number(targetPosterCm.toFixed(2)),
        pxPerCm: Number(pxPerCm.toFixed(2)),
        sourcePxPerCm: Number(sourcePxPerCm.toFixed(2)),
        targetPosterPxLen: Math.round(targetPosterPxLen),
        achievedPosterCm: Number(achievedPosterCm.toFixed(2)),
        clamped,
        flipX,
        imgPx: { w: iw, h: ih },
        drawPx: { w: Math.round(drawPxW), h: Math.round(drawPxH) },
      });
    }

    // Draw (mirror only the baby around its center)
    paintBaby(ctx, babyImg, x, y, drawW, drawH, flipX, style);
  }

  // -------------------------
  // Poster render
  // -------------------------

  // Clear + paper-colour fill over the ENTIRE physical buffer (identity
  // transform), not just the design rect: a print export renders onto a canvas
  // that is trim + bleed, and the paper colour has to run all the way to the
  // sheet edge or the trimmed-off strip comes out transparent/white.
  function paintPosterBackground(ctx, style) {
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.fillStyle = style.posterBgColor || "#FFFFFF";
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.restore();
  }

  function renderPoster(ctx, babyImg, input, styleOverride) {
    const style = { ...DEFAULT_STYLE, ...(styleOverride || {}) };

    const design = getDesignSize(input && input.sizeKey);
    const artboard = getArtboardRect(style, design);
    const footer = getFooterRect(style, artboard, design);

    // 1) Clear + WHITE full background (whole buffer, see paintPosterBackground)
    paintPosterBackground(ctx, style);

    // 2) Colored rect (artboard)
    ctx.fillStyle = style.rectColor || "#EDEBE7";
    ctx.fillRect(artboard.x, artboard.y, artboard.w, artboard.h);

    // 3) Baby inside rect
    drawBabyInsideArtboard(ctx, babyImg, input, style, artboard);

    // 4) Text on WHITE footer
    const centerX = design.w / 2;
    const maxWidth = design.w - style.footerPaddingX * 2;

    ctx.fillStyle = style.textColor;
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    const footerBottomY = footer.y + footer.h - style.footerPaddingBottom;

    // Name
    const name = String(input.name || "")
      .trim()
      .toUpperCase();
    const nameY = footerBottomY - style.NAME_Y_FROM_FOOTER_BOTTOM;

    const namePx = fitTextSize(
      ctx,
      name,
      maxWidth,
      style.nameSize,
      10,
      style.fontFamily,
    );
    ctx.font = `400 ${namePx}px ${style.fontFamily}, RockwellPoster`;
    drawSpacedText(ctx, name, centerX, nameY, style.letterSpacingName);

    console.log(input);

    const weightPart = (input.weightDisplay || "").trim();
    const lengthPart = (input.lengthDisplay || "").trim();
    // Meta
    const meta = [
      weightPart,
      lengthPart,
      input.dateStr ? String(input.dateStr).trim() : null,
    ]
      .filter(Boolean)
      .join("  |  ");

    const metaY = footerBottomY - style.META_Y_FROM_FOOTER_BOTTOM;
    ctx.font = `400 ${style.metaSize}px ${style.fontFamily}, RockwellPoster`;
    drawSpacedText(ctx, meta, centerX, metaY, style.letterSpacingMeta);
  }

  // -------------------------
  // Multi-baby poster (twins / triplets)
  // -------------------------

  // Visible silhouette of a design image: per image row, where the drawing starts
  // and ends (fractions of the image box). Measured once per image element on a
  // small scan canvas. Falls back to the whole image box when the canvas can not
  // be read (cross-origin image without CORS) or the scan fails.
  const PROFILE_SIDE = 384; // longest side of the scan canvas, px
  const PROFILE_ALPHA = 40; // alpha (0-255) above which a pixel is drawing
  const PROFILE_TRIM = 0.02; // share of a row's ink ignored at each end of the collision profile (stray leaf tips)
  const profileCache = typeof WeakMap === "function" ? new WeakMap() : null;

  function fullBoxProfile() {
    return { rows: 1, l: 0, r: 1, t: 0, b: 1, rowL: [0], rowR: [1], has: [1] };
  }

  // l/r/t/b = bounding box of the visible drawing; rowL/rowR = per-row ends of the
  // drawing with stray leaf tips trimmed (what two neighbours must not cross).
  function getVisibleProfile(img) {
    if (profileCache && profileCache.has(img)) return profileCache.get(img);
    let prof = null;
    try {
      const iw = img.naturalWidth || img.width;
      const ih = img.naturalHeight || img.height;
      if (iw > 0 && ih > 0 && typeof document !== "undefined") {
        const s = Math.min(1, PROFILE_SIDE / Math.max(iw, ih));
        const w = Math.max(1, Math.round(iw * s));
        const h = Math.max(1, Math.round(ih * s));
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        const cx = c.getContext("2d");
        cx.imageSmoothingEnabled = true;
        cx.imageSmoothingQuality = "high";
        cx.drawImage(img, 0, 0, w, h);
        const d = cx.getImageData(0, 0, w, h).data;
        const rowL = new Float32Array(h);
        const rowR = new Float32Array(h);
        const has = new Uint8Array(h);
        let bl = 1;
        let br = 0;
        let bt = -1;
        let bb = -1;
        for (let y = 0; y < h; y++) {
          const o = y * w * 4;
          let sum = 0;
          let first = -1;
          let last = -1;
          for (let x = 0; x < w; x++) {
            const a = d[o + x * 4 + 3];
            if (a > PROFILE_ALPHA) {
              sum += a;
              if (first < 0) first = x;
              last = x;
            }
          }
          if (first < 0) continue;
          has[y] = 1;
          const cut = sum * PROFILE_TRIM;
          let acc = 0;
          let tl = first;
          for (let x = first; x <= last; x++) {
            const a = d[o + x * 4 + 3];
            if (a > PROFILE_ALPHA) {
              acc += a;
              if (acc >= cut) {
                tl = x;
                break;
              }
            }
          }
          acc = 0;
          let tr = last;
          for (let x = last; x >= first; x--) {
            const a = d[o + x * 4 + 3];
            if (a > PROFILE_ALPHA) {
              acc += a;
              if (acc >= cut) {
                tr = x;
                break;
              }
            }
          }
          rowL[y] = tl / w;
          rowR[y] = (tr + 1) / w;
          if (first / w < bl) bl = first / w;
          if ((last + 1) / w > br) br = (last + 1) / w;
          if (bt < 0) bt = y;
          bb = y;
        }
        if (bt >= 0)
          prof = { rows: h, l: bl, r: br, t: bt / h, b: (bb + 1) / h, rowL, rowR, has };
      }
    } catch (_) {
      prof = null;
    }
    if (!prof) prof = fullBoxProfile();
    if (profileCache) profileCache.set(img, prof);
    return prof;
  }

  // Bounding box {l, r, t, b} (fractions of the image box) of the visible drawing.
  function getVisibleBox(img) {
    const p = getVisibleProfile(img);
    return { l: p.l, r: p.r, t: p.t, b: p.b };
  }

  function flipProfile(p) {
    const rowL = new Array(p.rows);
    const rowR = new Array(p.rows);
    for (let j = 0; j < p.rows; j++) {
      rowL[j] = 1 - p.rowR[j];
      rowR[j] = 1 - p.rowL[j];
    }
    return { rows: p.rows, l: 1 - p.r, r: 1 - p.l, t: p.t, b: p.b, rowL, rowR, has: p.has };
  }

  // Shrinks px (0.5 steps, letter spacing scaled with it) until the spaced text
  // fits maxWidth. Returns the font px and letter spacing to draw with; when the
  // text already fits at startPx that is exactly the single poster's look.
  function fitSpacedText(
    ctx,
    text,
    maxWidth,
    startPx,
    minPx,
    fontFamily,
    startSpacing,
  ) {
    let px = startPx;
    for (;;) {
      const spacing = startPx > 0 ? startSpacing * (px / startPx) : startSpacing;
      ctx.font = `400 ${px}px ${fontFamily}, RockwellPoster`;
      if (px <= minPx || spacedTextWidth(ctx, text, spacing) <= maxWidth)
        return { px, spacing };
      px -= 0.5;
    }
  }

  // Smallest x offset (px) of B's image box from A's image box (B drawn right of
  // A) at which B's drawing keeps clearPx from A's drawing on every row the two
  // share. A and B: { p: profile, W, H, y } in canvas px. -Infinity when they
  // share no row. padY (optional) widens each of A's rows by that much up and
  // down, so drawings only padY apart vertically also count as sharing a row.
  function minOffsetNoCollision(A, B, clearPx, padY) {
    let need = -Infinity;
    const pa = A.p;
    const pb = B.p;
    const pad = padY > 0 ? padY : 0;
    if (!(A.H > 0) || !(B.H > 0)) return need;
    for (let ja = 0; ja < pa.rows; ja++) {
      if (!pa.has[ja]) continue;
      const y0 = A.y + (ja / pa.rows) * A.H - pad;
      const y1 = A.y + ((ja + 1) / pa.rows) * A.H + pad;
      let j0 = Math.floor(((y0 - B.y) / B.H) * pb.rows);
      let j1 = Math.ceil(((y1 - B.y) / B.H) * pb.rows) - 1;
      if (j0 < 0) j0 = 0;
      if (j1 > pb.rows - 1) j1 = pb.rows - 1;
      const rightA = pa.rowR[ja] * A.W;
      for (let jb = j0; jb <= j1; jb++) {
        if (!pb.has[jb]) continue;
        const v = rightA - pb.rowL[jb] * B.W + clearPx;
        if (v > need) need = v;
      }
    }
    return need;
  }

  // Diagonal twins: a share of the baby height (drop or gap) that grows faster than the
  // babies. At the reference size (the taller drawing at multiDiagonalRefHeight of the
  // artboard height, about 50 cm on 50x70) it is `base`; smaller twins get less and
  // bigger ones more, by (size / reference) ^ multiDiagonalGrowth.
  function diagonalShare(style, base, vh, art) {
    const b = Math.max(0, Number(base) || 0);
    const ref = Math.max(0.01, Number(style.multiDiagonalRefHeight) || 0.6);
    const g = Math.max(0, Number(style.multiDiagonalGrowth) || 0);
    return b * Math.pow(Math.max(0, vh / art.h) / ref, g);
  }

  // One candidate layout of the babies at shared scale f, in canvas px.
  //  mode "side":    common baseline, small fixed inner gap between the visible
  //                  boxes, the group centred in the artboard.
  //  mode "stagger": babies alternate high / low (index 0, 2 high; 1 low), the low
  //                  ones dropped only as far as they must be (see the vertical
  //                  placement below), the group centred; neighbours sit as close as
  //                  their drawings allow (never touching, bounding boxes may
  //                  overlap by at most multiMaxOverlap of the narrower one), the
  //                  group centred, and the inner gap widens back up to the side
  //                  gap when there is room.
  // ok = the candidate fits the artboard (outer margins >= multiMinMargin).
  function buildMultiLayout(items, art, style, f, mode) {
    const n = items.length;
    const gIn = Math.max(0, Number(style.multiInnerGap) || 0) * art.w;
    const mMin = Math.max(0, Number(style.multiMinMargin) || 0) * art.w;
    const clear = Math.max(0, Number(style.multiRowClearance) || 0) * art.w;
    const mV = Math.max(0, Number(style.multiStaggerMarginY) || 0) * art.h;
    const maxOv = Math.max(0, Number(style.multiMaxOverlap) || 0);

    const bs = items.map((it) => {
      const W = it.w1 * f;
      const H = it.h1 * f;
      const p = it.prof;
      return { W, H, p, vw: (p.r - p.l) * W, vh: (p.b - p.t) * H, x: 0, y: 0 };
    });

    // vertical placement
    let maxVh = 0;
    for (const b of bs) if (b.vh > maxVh) maxVh = b.vh;
    let sumVw = 0;
    for (const b of bs) sumVw += b.vw;
    const avail = art.w - 2 * mMin - sumVw; // room for all inner gaps
    let heightOk;
    let lower = [];
    if (mode === "side") {
      const base = art.y + (art.h - maxVh) / 2 + maxVh;
      for (const b of bs) b.y = base - b.p.b * b.H;
      heightOk = maxVh <= art.h + 1e-6;
    } else {
      // Stagger as one compact group: the high babies' visible tops share a line,
      // the low babies' visible tops sit d below it, and the group is centred in
      // the artboard. d is the smallest drop at which the drawings keep the side
      // layout's inner gap on every shared row and the row still fits the width;
      // when no drop does, the low babies go to the bottom margin (d = dMax) with
      // the plain row clearance, the widest the stagger can spread.
      let maxHi = 0;
      let maxLo = 0;
      bs.forEach((b, i) => {
        if (i % 2 === 0) maxHi = Math.max(maxHi, b.vh);
        else maxLo = Math.max(maxLo, b.vh);
      });
      const dMax = art.h - 2 * mV - maxLo;
      const place = (d) => {
        const ext = Math.max(maxHi, d + maxLo);
        const top = art.y + (art.h - ext) / 2;
        bs.forEach((b, i) => {
          b.y = top + (i % 2 === 0 ? 0 : d) - b.p.t * b.H;
        });
      };
      const lowerAt = (d, clr) => {
        place(d);
        const ls = [];
        for (let i = 0; i < n - 1; i++) {
          const A = bs[i];
          const B = bs[i + 1];
          const need = minOffsetNoCollision(A, B, clr);
          const clearGap =
            need === -Infinity ? -Infinity : need + B.p.l * B.W - A.p.r * A.W;
          ls.push(Math.max(-maxOv * Math.min(A.vw, B.vw), clearGap));
        }
        return ls;
      };
      const fits = (ls) => ls.reduce((s, l) => s + l, 0) <= avail + 1e-6;
      const nice = Math.max(clear, gIn);
      let d = -1;
      if (n >= 2 && dMax >= 0) {
        const steps = 48;
        let prev = 0;
        for (let k = 0; k <= steps; k++) {
          const t = (dMax * k) / steps;
          if (fits(lowerAt(t, nice))) {
            let lo = prev;
            let hi = t;
            if (k > 0) {
              for (let q = 0; q < 20; q++) {
                const mid = (lo + hi) / 2;
                if (fits(lowerAt(mid, nice))) hi = mid;
                else lo = mid;
              }
            }
            d = hi;
            break;
          }
          prev = t;
        }
      }
      // "diagonal" (twins): the first item sits up-left, the next one lower and to the
      // right. The drop is a share of the babies' height that itself grows with their
      // size (diagonalShare), so small twins sit close in height and a bigger pair
      // grows apart, until it meets the margins.
      if (mode === "diagonal" && n >= 2) {
        // The gap is measured between the DRAWINGS (their bounding boxes may overlap,
        // the curled-up designs are much narrower than their boxes), and it grows with
        // the babies like the drop. When the pair is too wide at that drop, baby 2 goes
        // lower, only as far as needed.
        const vh = Math.max(maxHi, maxLo);
        let clr = Math.max(clear, diagonalShare(style, style.multiDiagonalGap, vh, art) * vh);
        const diagAt = (d) => {
          place(d);
          const ls = [];
          for (let i = 0; i < n - 1; i++) {
            const A = bs[i];
            const B = bs[i + 1];
            const need = minOffsetNoCollision(A, B, clr, clear);
            const clearGap = need === -Infinity ? -Infinity : need + B.p.l * B.W - A.p.r * A.W;
            ls.push(Math.max(-0.5 * Math.min(A.vw, B.vw), clearGap));
          }
          return ls;
        };
        const top = Math.max(0, dMax);
        // too wide even at the deepest drop: keep only the plain row clearance
        if (!fits(diagAt(top))) clr = clear;
        let dd = Math.min(top, diagonalShare(style, style.multiDiagonalDrop, vh, art) * vh);
        if (!fits(diagAt(dd))) {
          let lo = dd;
          let hi = top;
          if (fits(diagAt(hi))) {
            for (let q = 0; q < 24; q++) {
              const mid = (lo + hi) / 2;
              if (fits(diagAt(mid))) hi = mid;
              else lo = mid;
            }
          }
          dd = hi;
        }
        lower = diagAt(dd);
      }
      else if (d >= 0) lower = lowerAt(d, nice);
      else if (n >= 2) lower = lowerAt(Math.max(0, dMax), clear);
      else place(Math.max(0, dMax));
      heightOk = maxVh <= art.h - 2 * mV + 1e-6;
    }

    // horizontal placement
    let gaps = [];
    let widthOk;
    if (n < 2) {
      widthOk = avail >= -1e-6;
    } else if (mode === "side") {
      gaps = new Array(n - 1).fill(gIn);
      lower = gaps.slice();
      widthOk = avail >= (n - 1) * gIn - 1e-6;
    } else if (mode === "diagonal") {
      // the gaps already keep the diagonal's distance between the drawings
      gaps = lower.slice();
      widthOk = gaps.reduce((a, g) => a + g, 0) <= avail + 1e-6;
    } else {
      let glo = 0;
      let ghi = 0;
      for (const l of lower) {
        glo += l;
        ghi += Math.max(l, gIn);
      }
      widthOk = glo <= avail + 1e-6;
      let s;
      if (ghi <= avail) s = gIn;
      else if (!widthOk) s = -Infinity;
      else {
        let lo = Math.min(...lower);
        let hi = gIn;
        for (let k = 0; k < 40; k++) {
          const mid = (lo + hi) / 2;
          let g = 0;
          for (const l of lower) g += Math.max(l, Math.min(mid, gIn));
          if (g <= avail) lo = mid;
          else hi = mid;
        }
        s = lo;
      }
      gaps = lower.map((l) => Math.max(l, Math.min(s, gIn)));
    }

    let groupW = sumVw;
    for (const g of gaps) groupW += g;
    let vx = art.x + (art.w - groupW) / 2; // visible left edge of the next baby
    bs.forEach((b, i) => {
      b.x = vx - b.p.l * b.W;
      vx += b.vw + (i < gaps.length ? gaps[i] : 0);
    });

    return {
      ok: heightOk && widthOk,
      mode,
      columns: false,
      f,
      bs,
      gaps,
      margin: (art.w - groupW) / 2,
    };
  }

  // The push layout, used when neither the side layout nor the stagger fits at true
  // scale: babies alternate high / low like the stagger, but the bounding boxes may
  // overlap as far as the drawings allow (a later baby may slide under an earlier
  // one, never left of its visible left edge). Every pair of babies, not only
  // neighbours, keeps the side layout's inner gap between the drawings (falling
  // back to the plain row clearance), counted vertically as well. The low babies
  // drop the smallest distance that makes the group fit the width, then the group
  // is centred. ok = it fits the artboard with the usual margins.
  function buildPushLayout(items, art, style, f) {
    const n = items.length;
    const gIn = Math.max(0, Number(style.multiInnerGap) || 0) * art.w;
    const mMin = Math.max(0, Number(style.multiMinMargin) || 0) * art.w;
    const clear = Math.max(0, Number(style.multiRowClearance) || 0) * art.w;
    const mV = Math.max(0, Number(style.multiStaggerMarginY) || 0) * art.h;

    const bs = items.map((it) => {
      const W = it.w1 * f;
      const H = it.h1 * f;
      const p = it.prof;
      return { W, H, p, vw: (p.r - p.l) * W, vh: (p.b - p.t) * H, x: 0, y: 0 };
    });
    let maxVh = 0;
    let maxHi = 0;
    let maxLo = 0;
    bs.forEach((b, i) => {
      maxVh = Math.max(maxVh, b.vh);
      if (i % 2 === 0) maxHi = Math.max(maxHi, b.vh);
      else maxLo = Math.max(maxLo, b.vh);
    });
    const heightOk = maxVh <= art.h - 2 * mV + 1e-6;
    const dMax = Math.max(0, art.h - 2 * mV - maxLo);
    const room = art.w - 2 * mMin;

    // Places the babies for drop d and clearance clr; returns the visible width.
    const place = (d, clr) => {
      const ext = Math.max(maxHi, d + maxLo);
      const top = art.y + (art.h - ext) / 2;
      bs.forEach((b, i) => {
        b.y = top + (i % 2 === 0 ? 0 : d) - b.p.t * b.H;
        b.x = 0;
      });
      for (let j = 1; j < n; j++) {
        const B = bs[j];
        let x = -Infinity;
        for (let i = 0; i < j; i++) {
          const A = bs[i];
          const need = minOffsetNoCollision(A, B, clr, clr);
          const leftAligned = A.x + A.p.l * A.W - B.p.l * B.W;
          x = Math.max(x, A.x + Math.max(need, leftAligned - A.x));
        }
        B.x = x;
      }
      let l = Infinity;
      let r = -Infinity;
      for (const b of bs) {
        l = Math.min(l, b.x + b.p.l * b.W);
        r = Math.max(r, b.x + b.p.r * b.W);
      }
      return { l, w: r - l };
    };

    let ext = null;
    if (n >= 2 && heightOk) {
      for (const clr of [Math.max(clear, gIn), clear]) {
        const steps = 48;
        let prev = 0;
        for (let k = 0; k <= steps; k++) {
          const t = (dMax * k) / steps;
          if (place(t, clr).w <= room + 1e-6) {
            let lo = prev;
            let hi = t;
            if (k > 0) {
              for (let q = 0; q < 20; q++) {
                const mid = (lo + hi) / 2;
                if (place(mid, clr).w <= room + 1e-6) hi = mid;
                else lo = mid;
              }
            }
            ext = place(hi, clr);
            break;
          }
          prev = t;
        }
        if (ext) break;
      }
    }
    const ok = !!ext;
    if (!ext) ext = place(dMax, clear);
    const shift = art.x + (art.w - ext.w) / 2 - ext.l;
    for (const b of bs) b.x += shift;
    const gaps = [];
    for (let i = 0; i < n - 1; i++) {
      const A = bs[i];
      const B = bs[i + 1];
      gaps.push(B.x + B.p.l * B.W - (A.x + A.p.r * A.W));
    }
    return {
      ok,
      mode: "stagger",
      columns: false,
      f,
      bs,
      gaps,
      margin: (art.w - ext.w) / 2,
    };
  }

  // Plain equal columns, the first version's layout: N equal columns across the
  // artboard, each baby's whole image box centred in its column, all babies on one
  // bottom edge (group centred in the artboard height). ONE shared factor
  // f <= 1, only below 1 when a baby is taller than the artboard or wider than its
  // column. The whole image box counts here (no visible-drawing profile), so a baby's
  // "visible" box is its image box.
  function buildColumnsLayout(items, art) {
    const n = items.length;
    const colPxW = art.w / n;
    const availPxW = Math.max(1, colPxW);
    let f = 1;
    for (const it of items) {
      if (it.h1 > art.h) f = Math.min(f, art.h / it.h1);
      if (it.w1 > availPxW) f = Math.min(f, availPxW / it.w1);
    }
    let maxPxH = 0;
    for (const it of items) maxPxH = Math.max(maxPxH, it.h1 * f);
    const topPx = art.y + (art.h - maxPxH) / 2;
    const bs = items.map((it, i) => {
      const W = it.w1 * f;
      const H = it.h1 * f;
      const p = fullBoxProfile();
      return {
        W,
        H,
        p,
        vw: (p.r - p.l) * W,
        vh: (p.b - p.t) * H,
        x: art.x + colPxW * i + (colPxW - W) / 2,
        y: topPx + (maxPxH - H),
      };
    });
    const gaps = [];
    for (let i = 1; i < n; i++) gaps.push(bs[i].x - (bs[i - 1].x + bs[i - 1].W));
    return {
      ok: true,
      mode: "side",
      columns: true,
      captionsColumns: true,
      f,
      bs,
      gaps,
      margin: 0,
    };
  }

  // Picks the layout. auto: side by side at true scale when it fits (visible
  // boxes, inner gap, outer margins), otherwise the stagger; either way ONE
  // shared factor f <= 1 that is only below 1 when even that layout does not fit.
  function solveMultiLayout(items, art, style) {
    const n = items.length;
    const search = (mode) => {
      const full = buildMultiLayout(items, art, style, 1, mode);
      if (full.ok) return full;
      let lo = 0.05;
      let hi = 1;
      let best = null;
      for (let k = 0; k < 30; k++) {
        const mid = (lo + hi) / 2;
        const t = buildMultiLayout(items, art, style, mid, mode);
        if (t.ok) {
          best = t;
          lo = mid;
        } else hi = mid;
      }
      return best || buildMultiLayout(items, art, style, lo, mode);
    };
    const pref = style.multiLayout;
    if (pref === "columns") return buildColumnsLayout(items, art);
    // Every count except two keeps the plain equal columns as long as no baby
    // overflows its column; twins never use them, and an overflowing baby sends the
    // poster to the compact group / stagger below.
    if (pref !== "side" && pref !== "stagger" && n !== 2) {
      const cols = buildColumnsLayout(items, art);
      if (cols.f === 1) return cols;
    }
    if (n < 2 || pref === "side") return search("side");
    if (pref === "stagger") return search("stagger");
    if (pref === "diagonal") return search("diagonal");
    // Twins: diagonal first (Matilda, 2026-10-20). "classic" is the launch layout: what
    // Lines, Still and Contour twins keep, and what orders placed before the change
    // print with, so they match what the customer saw.
    if (n === 2 && pref !== "classic") {
      const diag = buildMultiLayout(items, art, style, 1, "diagonal");
      if (diag.ok) return diag;
    }
    const side = buildMultiLayout(items, art, style, 1, "side");
    if (side.ok) return side;
    const stagger = buildMultiLayout(items, art, style, 1, "stagger");
    if (stagger.ok) return stagger;

    // One baby's size never changes another's: a baby taller than the artboard
    // (minus the push margins) shrinks alone to fit, then the babies are pushed
    // apart at true scale, first within the usual margins, then within the
    // narrower push margins. Only when even that does not fit do all of them
    // shrink by one shared factor, as little as possible.
    const tight = {
      ...style,
      multiMinMargin: style.multiPushMinMargin,
      multiStaggerMarginY: style.multiPushMarginY,
    };
    const mV = Math.max(0, Number(tight.multiStaggerMarginY) || 0) * art.h;
    const own = items.map((it) => {
      const vh = (it.prof.b - it.prof.t) * it.h1;
      const c = vh > 0 ? Math.min(1, (art.h - 2 * mV) / vh) : 1;
      return c < 1 ? { ...it, w1: it.w1 * c, h1: it.h1 * c } : it;
    });
    if (own.some((it, i) => it !== items[i])) {
      const sideOwn = buildMultiLayout(own, art, style, 1, "side");
      if (sideOwn.ok) return sideOwn;
    }
    const push = buildPushLayout(own, art, style, 1);
    if (push.ok) return push;
    const pushTight = buildPushLayout(own, art, tight, 1);
    if (pushTight.ok) return pushTight;
    let lo = 0.05;
    let hi = 1;
    let best = null;
    for (let k = 0; k < 30; k++) {
      const mid = (lo + hi) / 2;
      const t = buildPushLayout(own, art, tight, mid);
      if (t.ok) {
        best = t;
        lo = mid;
      } else hi = mid;
    }
    return best || buildPushLayout(own, art, tight, lo);
  }

  // Twins that go diagonal: the curled-up Wreath (Elsa, Tove, Hilma) and Soft (Isak,
  // Knut, Edvin) designs, which looked wrong side by side (Matilda, 2026-10-20). Lines,
  // Still and Contour keep the launch layout. Block ids of the design blocks, the same
  // ids the twins page lists per collection. Storefront babyType is the id, admin's is
  // "<id>#<slot>".
  const DIAGONAL_TWIN_DESIGNS = [
    "baby_design_4iL9Ue", "baby_design_aJ8hxy", "baby_design_YzWqkz", // Wreath
    "baby_design_RtWcpJ", "baby_design_Lkx7Hb", "baby_design_qEfGaE"  // Soft
  ];

  // N babies on ONE artboard, one caption per baby in the footer.
  //  - Every baby is drawn at true scale (lengthCm x its model's factor), times ONE
  //    shared factor f <= 1 (1 unless even the chosen layout does not fit).
  //  - Layout is chosen per poster (style.multiLayout "auto" | "side" | "stagger"),
  //    see solveMultiLayout; it works on the visible drawing of each design, not the
  //    padded image box.
  //  - Captions sit centred under each baby's drawn position when they do not
  //    collide, otherwise in N equal columns of the artboard.
  // Returns the computed layout (design units) for callers that want to inspect it.
  function renderMultiPoster(ctx, babyImgs, input, styleOverride) {
    const style = { ...DEFAULT_STYLE, ...(styleOverride || {}) };

    const sizeKey = (input && input.sizeKey) || "50x70";
    const design = getDesignSize(sizeKey);
    const artboard = getArtboardRect(style, design);
    const footer = getFooterRect(style, artboard, design);

    const babies = input && Array.isArray(input.babies) ? input.babies : [];
    const imgs = Array.isArray(babyImgs) ? babyImgs : [];
    const n = babies.length;

    // 1) Clear + WHITE full background, 2) one coloured artboard across the width
    paintPosterBackground(ctx, style);
    ctx.fillStyle = style.rectColor || "#EDEBE7";
    ctx.fillRect(artboard.x, artboard.y, artboard.w, artboard.h);

    const layout = {
      sizeKey,
      design,
      artboard,
      mode: "side",
      arrangement: "group",
      f: 1,
      gaps: [],
      overlaps: [],
      captions: { mode: "columns", items: [] },
      babies: [],
    };
    if (!n) return layout;

    // All sizing below is done in canvas pixels and converted back to DESIGN
    // space for drawing (same as the single poster), so nothing is ever squished.
    const tr = ctx.getTransform();
    const sx = tr.a || 1;
    const sy = tr.d || 1;
    const art = {
      x: artboard.x * sx,
      y: artboard.y * sy,
      w: artboard.w * sx,
      h: artboard.h * sy,
    };

    // 3) True-scale size and visible silhouette of each baby, then the layout
    const items = babies.map((b, i) => {
      const baby = b || {};
      const img = imgs[i];
      const hasImg = !!(img && (img.naturalWidth || img.width));
      const m = hasImg ? computeBabyMetrics(ctx, img, baby, sizeKey) : null;
      const model = m ? m.model : getBabyModel(baby.babyType);
      const flipX = !!(baby.direction !== model.direction);
      let prof = fullBoxProfile();
      if (m && style.multiLayout !== "columns") {
        prof = getVisibleProfile(img);
        if (flipX) prof = flipProfile(prof);
      }
      return {
        baby,
        img,
        m,
        flipX,
        prof,
        w1: m ? m.drawPxW : 0,
        h1: m ? m.drawPxH : 0,
      };
    });

    // Twins drawn standing straight (the Contour collection) stay side by side
    // (Matilda, 2026-10-20). A mixed pair goes diagonal like every other pair.
    const designOf = (b0) => String((b0 && b0.babyType) || "").split("#")[0];
    const straight = n === 2 && style.multiLayout === "auto" &&
      !babies.some((b0) => DIAGONAL_TWIN_DESIGNS.indexOf(designOf(b0)) !== -1);
    const sol = solveMultiLayout(items, art, straight ? { ...style, multiLayout: "classic" } : style);
    layout.mode = sol.mode;
    layout.arrangement =
      sol.mode === "stagger" || sol.mode === "diagonal" ? "stagger" : sol.columns ? "columns" : "group";
    layout.f = sol.f;

    // common baseline of the visible feet (side mode only)
    if (sol.mode === "side") {
      let base = 0;
      sol.bs.forEach((b) => {
        base = Math.max(base, b.y + b.p.b * b.H);
      });
      layout.baselineY = base / sy;
    }

    // 4) Draw left to right (a later baby is drawn over an earlier one)
    sol.bs.forEach((b, i) => {
      const it = items[i];
      const x = b.x / sx;
      const y = b.y / sy;
      const drawW = b.W / sx;
      const drawH = b.H / sy;
      layout.babies.push({
        index: i,
        x,
        y,
        w: drawW,
        h: drawH,
        flipX: it.flipX,
        scale: it.w1 > 0 ? b.W / it.w1 : 1, // this baby's drawn size / true size
        vis: {
          x: (b.x + b.p.l * b.W) / sx,
          y: (b.y + b.p.t * b.H) / sy,
          w: b.vw / sx,
          h: b.vh / sy,
        },
      });
      if (it.m) paintBaby(ctx, it.img, x, y, drawW, drawH, it.flipX, style);
    });
    layout.gaps = sol.gaps.map((g) => g / sx);
    sol.gaps.forEach((g, i) => {
      if (g < 0) {
        const narrow = Math.min(sol.bs[i].vw, sol.bs[i + 1].vw);
        layout.overlaps.push({
          pair: [i, i + 1],
          design: -g / sx,
          frac: narrow > 0 ? -g / narrow : 0,
        });
      }
    });

    if (style.debug) {
      console.log("[Multi baby render debug]", {
        sizeKey,
        babies: n,
        mode: sol.mode,
        sharedFactor: Number(sol.f.toFixed(4)),
        pxPerCm: Number(getPosterPxPerCm(ctx, sizeKey).toFixed(2)),
        drawPx: sol.bs.map((b) => ({ w: Math.round(b.W), h: Math.round(b.H) })),
        gapsDesign: layout.gaps.map((g) => Number(g.toFixed(1))),
      });
    }

    // 5) One caption per baby on the WHITE footer
    ctx.fillStyle = style.textColor;
    ctx.textAlign = "left";
    ctx.textBaseline = "alphabetic";

    const colW = artboard.w / n;
    const capMaxW = Math.max(
      1,
      colW - 2 * Math.max(0, Number(style.multiCaptionPadX) || 0),
    );
    const footerBottomY = footer.y + footer.h - style.footerPaddingBottom;
    const nameY = footerBottomY - style.NAME_Y_FROM_FOOTER_BOTTOM;
    const metaY = footerBottomY - style.META_Y_FROM_FOOTER_BOTTOM;

    const texts = babies.map((b0) => {
      const b = b0 || {};
      return {
        name: String(b.name || "")
          .trim()
          .toUpperCase(),
        meta: [
          (b.weightDisplay || "").trim(),
          (b.lengthDisplay || "").trim(),
          b.dateStr ? String(b.dateStr).trim() : null,
        ]
          .filter(Boolean)
          .join("  |  "),
      };
    });

    // centred under the drawn baby, if no two captions (at the single poster's
    // size) would touch each other or leave the artboard; else equal columns
    const centres = sol.bs.map((b) => (b.x + ((b.p.l + b.p.r) / 2) * b.W) / sx);
    const widths = texts.map((t) => {
      ctx.font = `400 ${style.nameSize}px ${style.fontFamily}, RockwellPoster`;
      const wn = spacedTextWidth(ctx, t.name, style.letterSpacingName);
      ctx.font = `400 ${style.metaSize}px ${style.fontFamily}, RockwellPoster`;
      const wm = spacedTextWidth(ctx, t.meta, style.letterSpacingMeta);
      return Math.max(wn, wm);
    });
    const capGap = Math.max(0, Number(style.multiCaptionGap) || 0);
    // Captions follow the babies left to right on the paper, whatever the order of the
    // list, so a name always sits under its own baby.
    const byX = centres.map((c, i) => i).sort((a, b) => centres[a] - centres[b]);
    const rank = [];
    byX.forEach((i, k) => { rank[i] = k; });
    let actual = true;
    for (let k = 0; k < n; k++) {
      const i = byX[k];
      if (
        centres[i] - widths[i] / 2 < artboard.x ||
        centres[i] + widths[i] / 2 > artboard.x + artboard.w
      )
        actual = false;
      const h = k > 0 ? byX[k - 1] : -1;
      if (h >= 0 && centres[i] - centres[h] < (widths[i] + widths[h]) / 2 + capGap)
        actual = false;
    }
    if (sol.captionsColumns) actual = false;
    layout.captions.mode = actual ? "actual" : "columns";

    texts.forEach((t, i) => {
      const centerX = actual ? centres[i] : artboard.x + colW * (rank[i] + 0.5);
      const maxW = actual ? Infinity : capMaxW;
      layout.captions.items.push({ cx: centerX, w: widths[i] });

      const nameFit = fitSpacedText(
        ctx,
        t.name,
        maxW,
        style.nameSize,
        10,
        style.fontFamily,
        style.letterSpacingName,
      );
      ctx.font = `400 ${nameFit.px}px ${style.fontFamily}, RockwellPoster`;
      drawSpacedText(ctx, t.name, centerX, nameY, nameFit.spacing);

      const metaFit = fitSpacedText(
        ctx,
        t.meta,
        maxW,
        style.metaSize,
        6,
        style.fontFamily,
        style.letterSpacingMeta,
      );
      ctx.font = `400 ${metaFit.px}px ${style.fontFamily}, RockwellPoster`;
      drawSpacedText(ctx, t.meta, centerX, metaY, metaFit.spacing);
    });

    return layout;
  }

  // -------------------------
  // Exports
  // -------------------------
  function getPreviewPixels({ longSide = 1200 } = {}) {
    const ratio = DESIGN_H / DESIGN_W; // jpg preview helper (legacy)
    const w = longSide;
    const h = Math.round(longSide * ratio);
    return { w, h };
  }

  // Pass `babyImgs` (array, same order as input.babies) instead of `babyImg` to
  // export the multi-baby poster; with `babyImg` this is the single poster as ever.
  async function exportJpg({
    input,
    babyImg,
    babyImgs,
    style,
    longSide = 1200,
    quality = 0.9,
    sizeKey,
  }) {
    const multi = Array.isArray(babyImgs);
    // Multi inputs carry their size in input.sizeKey; single callers pass sizeKey.
    const key = multi ? sizeKey || (input && input.sizeKey) : sizeKey;

    let w, h;
    if (key && SIZE_MM[key]) {
      const mm = SIZE_MM[key];
      if (mm.w > mm.h) {
        // landscape: the long side is the width
        w = longSide;
        h = Math.round((longSide * mm.h) / mm.w);
      } else {
        const ratio = mm.h / mm.w;
        h = longSide;
        w = Math.round(longSide / ratio);
      }
    } else {
      const px = getPreviewPixels({ longSide });
      w = px.w;
      h = px.h;
    }
    const inputWithSize = { ...(input || {}), sizeKey: key || (input && input.sizeKey) };
    const { off, offCtx } = makeOffscreenCanvas(w, h, key);
    if (multi) renderMultiPoster(offCtx, babyImgs, inputWithSize, style);
    else renderPoster(offCtx, babyImg, inputWithSize, style);
    return off.toDataURL("image/jpeg", quality);
  }

  // Pass `babyImgs` instead of `babyImg` for the multi-baby poster (size may then
  // come from input.sizeKey); with `babyImg` this is the single poster as ever.
  async function exportPdf({
    input,
    babyImg,
    babyImgs,
    style,
    size,
    dpi = 300,
    jpegQuality = 0.95,
  }) {
    if (!window.jspdf || !window.jspdf.jsPDF) {
      throw new Error(
        "jsPDF not loaded. Add the jsPDF script tag on this page.",
      );
    }

    const multi = Array.isArray(babyImgs);
    if (multi) size = size || (input && input.sizeKey);

    const mm = SIZE_MM[size];
    if (!mm) throw new Error("Unknown size for PDF: " + size);

    const pxW = mmToPx(mm.w, dpi);
    const pxH = mmToPx(mm.h, dpi);

    const { off, offCtx } = makeOffscreenCanvas(pxW, pxH, size);

    // Ensure sizeKey exists for physical cm math
    const inputWithSize = { ...(input || {}), sizeKey: size };

    if (multi) renderMultiPoster(offCtx, babyImgs, inputWithSize, style);
    else renderPoster(offCtx, babyImg, inputWithSize, style);

    const imgData = off.toDataURL("image/jpeg", jpegQuality);

    const { jsPDF } = window.jspdf;
    const pdf = new jsPDF({
      orientation: mm.h >= mm.w ? "portrait" : "landscape",
      unit: "mm",
      format: [mm.w, mm.h],
    });

    pdf.addImage(imgData, "JPEG", 0, 0, mm.w, mm.h);
    return pdf;
  }

  function dataUrlToBlob(dataUrl) {
    const parts = dataUrl.split(",");
    const mime = parts[0].match(/:(.*?);/)[1];
    const bin = atob(parts[1]);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
  }

  function calcPrintLongSide(sizeKey, dpi) {
    dpi = dpi || 300;
    const mm = SIZE_MM[sizeKey] || SIZE_MM["50x70"];
    var longMm = Math.max(mm.w, mm.h);
    return Math.round((longMm / 25.4) * dpi);
  }

  return {
    DESIGN_W,
    DESIGN_H,
    SIZE_MM,
    DEFAULT_STYLE,

    getDesignSize,

    setupPreviewCanvas,
    setupPreviewCanvasForSize,
    ensureFontLoaded,

    loadImageTo,
    renderPoster,
    renderMultiPoster,
    getVisibleBox,
    upsertBabyModel,
    setDefaultBabyKey,

    exportJpg,
    exportPdf,

    downloadDataUrl,
    safeFilename,

    // print originals (admin)
    setPrintSource,

    // dynamic model API
    setBabyModelAssetUrl,
    getBabyModel,
    setBabyModelCalibration,

    // print helpers
    dataUrlToBlob,
    calcPrintLongSide,
  };
});
